import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { getStoredReferrerId, clearStoredReferrerId } from "../components/ReferralTracker";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    // AuthCallback will exchange the session_id and establish session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithCredentials = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    // Backend sets an httpOnly cookie automatically. We no longer store the token
    // in localStorage (XSS protection). Legacy token cleanup :
    localStorage.removeItem("auth_token");
    setUser(res.data.user);
    return res.data.user;
  };

  const registerWithCredentials = async (email, password, name) => {
    const referrerId = getStoredReferrerId();
    const payload = { email, password, name };
    if (referrerId) payload.referrer_id = referrerId;
    const res = await api.post("/auth/register", payload);
    // Same as login : cookie is set by backend. No localStorage write.
    localStorage.removeItem("auth_token");
    if (referrerId) clearStoredReferrerId();
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    // Clear any legacy localStorage token in case it survived
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, loginWithCredentials, registerWithCredentials, logout, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
